import { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { ExternalLink, ShieldAlert } from "lucide-react";
import { useLang } from "@/i18n";
import { SITE, fmtInt } from "@/config/site";
import { PageHero, Reveal, CopyButton } from "@/components/common";

const ALLOC = [
  { key: "liquidity", labelKey: "tok.liquidity", pct: 40, color: "#009B3A",
    purpose: { en: "DEX liquidity and exchange listings.", pt: "Liquidez em DEX e listagens em exchanges." } },
  { key: "social", labelKey: "tok.social", pct: 20, color: "#A8FF3E",
    purpose: { en: "Social fund, utilities and community programs.", pt: "Fundo social, utilidades e programas comunitários." } },
  { key: "marketing", labelKey: "tok.marketing", pct: 15, color: "#D4AF37",
    purpose: { en: "Marketing, creators and brand growth.", pt: "Marketing, criadores e crescimento da marca." } },
  { key: "team", labelKey: "tok.team", pct: 10, color: "#F5D778",
    purpose: { en: "Team and development — subject to vesting.", pt: "Equipe e desenvolvimento — sujeito a vesting." } },
  { key: "rewards", labelKey: "tok.rewards", pct: 10, color: "#4ade80",
    purpose: { en: "Ecosystem rewards and participation incentives.", pt: "Recompensas do ecossistema e incentivos de participação." } },
  { key: "partners", labelKey: "tok.partners", pct: 5, color: "#3b82f6",
    purpose: { en: "Strategic partnerships.", pt: "Parcerias estratégicas." } },
];

const VESTING = { alloc: 90_000_000, cliff: 6, linear: 30, total: 36 };

export default function Tokenomics() {
  const { t, lang } = useLang();
  const [active, setActive] = useState(0);
  const sel = ALLOC[active];
  const amount = (SITE.totalSupply * sel.pct) / 100;

  return (
    <>
      <PageHero eyebrow={t("tok.eyebrow")} title={t("tok.title")} sub={t("tok.sub")} />

      <section className="section-pad grid items-start gap-8 lg:grid-cols-[1fr_1fr]" data-testid="tokenomics-donut">
        <Reveal className="card-surface p-6">
          <div className="h-[340px] w-full">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={ALLOC}
                  dataKey="pct"
                  nameKey="key"
                  innerRadius="58%"
                  outerRadius="88%"
                  paddingAngle={3}
                  stroke="#050807"
                  strokeWidth={3}
                  onClick={(_, i) => setActive(i)}
                  className="cursor-pointer outline-none"
                >
                  {ALLOC.map((a, i) => (
                    <Cell key={a.key} fill={a.color} opacity={active === i ? 1 : 0.4}
                      style={{ transition: "opacity .3s" }} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#0B120E", border: "1px solid rgba(212,175,55,.3)", borderRadius: 12, fontFamily: "JetBrains Mono" }}
                  itemStyle={{ color: "#F8F7F2" }}
                  formatter={(v, name, item) => [`${v}% · ${fmtInt((SITE.totalSupply * v) / 100)} MBGA`, t(item.payload.labelKey)]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ALLOC.map((a, i) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setActive(i)}
                data-testid={`alloc-${a.key}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left font-heading text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                  active === i ? "border-neon/50 bg-neon/5 text-paper" : "border-white/10 text-mist hover:border-white/25"
                }`}
              >
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: a.color }} />
                {t(a.labelKey)}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="card-surface p-7" data-testid="alloc-detail">
          <p className="eyebrow">{t("tok.category")}</p>
          <h3 className="mt-3 font-heading text-2xl font-bold uppercase text-paper">{t(sel.labelKey)}</h3>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-mist">{t("tok.pct")}</p>
              <p className="mt-1 font-mono text-3xl font-bold" style={{ color: sel.color }}>{sel.pct}%</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-mist">{t("tok.amount")}</p>
              <p className="mt-1 font-mono text-3xl font-bold text-paper">{fmtInt(amount)}</p>
            </div>
          </div>
          <div className="mt-6 space-y-4 border-t border-white/10 pt-6 text-sm">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-mist">{t("tok.purpose")}</p>
              <p className="mt-1 text-paper/90">{sel.purpose[lang]}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-mist">{t("tok.allocStatus")}</p>
              <p className="mt-1 text-goldlight">{t("tok.allocPlanned")}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-mist">{t("tok.wallet")}</p>
              <p className="mt-1 text-mist">{t("tok.walletPending")}</p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="section-pad mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="supply-cards">
        {[
          { label: t("tok.circulating"), value: t("common.awaitingLaunch"), mono: true },
          { label: t("tok.locked"), value: t("common.awaitingLaunch"), mono: true },
          { label: t("tok.nextUnlock"), value: t("common.awaitingLaunch"), mono: true },
          { label: t("tok.treasury"), value: t("common.notDeployed"), mono: true },
        ].map((c) => (
          <Reveal key={c.label}>
            <div className="card-surface p-5">
              <p className="font-heading text-[10px] font-bold uppercase tracking-[0.25em] text-mist">{c.label}</p>
              <p className="mt-3 font-mono text-sm font-bold uppercase tracking-wider text-goldlight/90">{c.value}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="section-pad mt-8 grid gap-6 lg:grid-cols-2">
        <Reveal className="card-surface p-7" data-testid="vesting-card">
          <p className="eyebrow">{t("tok.vestingTitle")}</p>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { l: t("tok.vestingAlloc"), v: `${fmtInt(VESTING.alloc)}` },
              { l: t("tok.vestingCliff"), v: `${VESTING.cliff} mo` },
              { l: t("tok.vestingLinear"), v: `${VESTING.linear} mo` },
              { l: t("tok.vestingTotal"), v: `${VESTING.total} mo` },
            ].map((x) => (
              <div key={x.l}>
                <p className="font-mono text-[10px] uppercase tracking-widest text-mist">{x.l}</p>
                <p className="mt-1 font-mono text-lg font-bold text-paper">{x.v}</p>
              </div>
            ))}
          </div>
          <div className="mt-7">
            <div className="flex h-4 overflow-hidden rounded-full border border-white/10 bg-ink">
              <div className="h-full bg-gold/60" style={{ width: `${(VESTING.cliff / VESTING.total) * 100}%` }} title="Cliff" />
              <div className="h-full bg-gradient-to-r from-brgreen to-neon/70 opacity-70" style={{ width: `${(VESTING.linear / VESTING.total) * 100}%` }} title="Linear" />
            </div>
            <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-widest text-mist">
              <span>0</span><span className="text-goldlight">Cliff · {VESTING.cliff}mo</span><span className="text-neon">Linear · {VESTING.linear}mo</span><span>{VESTING.total}mo</span>
            </div>
          </div>
          <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-mist">
            <ShieldAlert size={14} className="mt-0.5 shrink-0 text-gold" />
            {t("tok.vestingNote")}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="card-surface flex flex-col p-7" data-testid="contract-card">
          <p className="eyebrow">{t("tok.contractTitle")}</p>
          <div className="mt-5 flex-1 rounded-xl border border-dashed border-gold/30 bg-ink/60 p-6 text-center">
            {SITE.mintAddress ? (
              <>
                <p className="break-all font-mono text-sm text-neon">{SITE.mintAddress}</p>
                <div className="mt-4 flex justify-center gap-3">
                  <CopyButton value={SITE.mintAddress} />
                  <a href={`https://solscan.io/token/${SITE.mintAddress}`} target="_blank" rel="noopener noreferrer" className="mono-chip border-neon/40 text-neon">
                    <ExternalLink size={12} /> {t("common.viewSolscan")}
                  </a>
                </div>
              </>
            ) : (
              <>
                <p className="font-display text-3xl uppercase tracking-wide text-goldlight/80" data-testid="contract-status">
                  {t("tok.notDeployedYet")}
                </p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-mist">{SITE.network}</p>
              </>
            )}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-mist">{t("tok.contractHint")}</p>
        </Reveal>
      </section>
    </>
  );
}
